import React, { useMemo, useState } from 'react';

import pluralize from 'pluralize';

import {
  Checkbox,
  Chip,
  FormControl,
  FormLabel,
  ListSubheader,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

interface widgetProps {
  name: string;
  property: App.Bedbank.PropertyEdit;
  internalValue: Array<string>;
  activePromotions: App.Bedbank.PromotionsEdit[];
  setRooms: (index: string | number, rooms: Array<string>, append?: boolean) => void;
}

const containsText = (text, searchText) => {
  if (Array.isArray(searchText)) {
    return searchText.some((term) => text.toLowerCase().indexOf(term.toLowerCase()) > -1);
  }
  return text.toLowerCase().indexOf(searchText.toLowerCase()) > -1;
};

const FIELD_NAME_REGEX = /\w+_(\d+)_restrictToRooms/;

export default function PromotionRoomMultiSelect({
  name,
  property,
  activePromotions,
  internalValue,
  setRooms,
}: widgetProps) {
  const [searchText, setSearchText] = useState('');

  const promotionIndex = useMemo(() => {
    return name.match(FIELD_NAME_REGEX)[1];
  }, [name]);

  const roomsEnum = useMemo(() => {
    const supplier = activePromotions[promotionIndex]?.supplier;
    return property.rooms
      .map((room) => ({
        id: room.id,
        name: room.nameOverride ?? room.name,
        externalId: room.externalId,
        suppliers: Array.from(
          new Set((room.mappedRooms ?? [{ supplier: room.supplier }]).map((x) => x.supplier)).values(),
        ),
      }))
      .filter((x) => x.suppliers.includes(supplier));
  }, [property, activePromotions, promotionIndex]);

  const displayedRooms = useMemo(
    () =>
      roomsEnum.filter(
        (room) => containsText(room.id, searchText.split(',')) || containsText(room.name, searchText.split(',')),
      ),
    [roomsEnum, searchText],
  );

  const selectedOptions = useMemo(() => {
    return internalValue || [];
  }, [internalValue]);

  const handleChange = (event) => {
    const value = event.target.value;

    if (value[value.length - 1] === 'all') {
      setRooms(promotionIndex, selectedOptions.length === roomsEnum.length ? [] : roomsEnum.map((x) => x.id));
      return;
    }
    setRooms(promotionIndex, value);
  };

  const renderSelectedValue = () => {
    if (selectedOptions.length === roomsEnum.length) {
      return 'All rooms are selected';
    }
    if (selectedOptions.length) {
      const maxDisplayed = 3;
      return selectedOptions
        .map((x) => roomsEnum.find((n) => n.id === x).name)
        .slice(0, maxDisplayed)
        .join(', ')
        .concat(
          selectedOptions.length > maxDisplayed
            ? ` and ${pluralize('more rooms', selectedOptions.length - maxDisplayed, true)} are selected`
            : '',
        );
    }
    return 'Select some rooms...';
  };

  return (
    <FormControl fullWidth>
      <FormLabel>Restrict to Rooms</FormLabel>
      <Select
        MenuProps={{
          autoFocus: false,
          PaperProps: { sx: { maxHeight: 300 } },
        }}
        value={selectedOptions}
        onChange={handleChange}
        onClose={() => setSearchText('')}
        renderValue={renderSelectedValue}
        multiple
        displayEmpty
      >
        <ListSubheader>
          <TextField
            size="small"
            autoFocus
            placeholder="Search..."
            fullWidth
            onChange={(e) => setSearchText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key !== 'Escape') {
                e.stopPropagation();
              }
            }}
          />
        </ListSubheader>
        <MenuItem value="all">
          <Checkbox checked={selectedOptions.length === roomsEnum.length} />
          Select all
        </MenuItem>
        {displayedRooms.map((room, i) => (
          <MenuItem key={i} value={room.id}>
            <Checkbox checked={selectedOptions.includes(room.id)} />
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography fontWeight="bold">{room.name}</Typography>
              <Typography>{room.externalId}</Typography>
              {room.suppliers.map((supplier) => (
                <Chip key={supplier} label={supplier} />
              ))}
            </Stack>
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}
